import { useMemo, useState, type ReactNode } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ArrowLeft, Upload } from 'lucide-react';

import { Button } from '@g17eco/ui-modern/components/ui/button';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { Dashboard } from '@g17eco/ui-modern/components/ui/dashboard';
import { NoData } from '@g17eco/molecules/no-data';
import { DeleteModal } from '@g17eco/molecules/confirm-modal';
import { toastFromError } from '@g17eco/molecules/toasts';

import { LibraryUploadModal } from '../components/LibraryUploadModal';
import { StatsStrip } from '../components/library/StatsStrip';
import { Toolbar } from '../components/library/Toolbar';
import { ListHeader } from '../components/library/ListHeader';
import { FilterSummary } from '../components/library/FilterSummary';
import { EvidenceLibraryRow } from '../components/library/EvidenceLibraryRow';
import { DeleteDialogContent, deleteDialogTitle } from '../components/library/DeleteDialog';
import { LinkedActivitiesModal } from '../components/library/LinkedActivitiesModal';
import { deriveItems, sortItems, type SortKey } from '../components/library/librarySort';

import {
  useDeleteEmissionEvidenceMutation,
  useListEmissionEvidenceQuery,
} from '../api/emissions';
import { useDownloadEvidence } from '../hooks/useDownloadEvidence';
import { EvidenceType, type EmissionEvidenceListItem } from '../types/emissions';
import { summarizeOutcomes, type Outcome } from '../utils/summarizeOutcomes';
import { bytesToHumanReadable } from '../../../utils';
import { emissionsTrackerRoutes } from '../routes';
import { generateUrl } from '../../../routes/util';

const PAGE_SIZE = 50;

export const EmissionsEvidenceLibraryPage = () => {
  const { initiativeId = '' } = useParams<{ initiativeId: string }>();

  const [evidenceType, setEvidenceType] = useState<EvidenceType | ''>('');
  // Server has no index for file extension — narrows the current page only.
  const [fileType, setFileType] = useState<string>('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('recent');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<EmissionEvidenceListItem | null>(null);
  const [linkedFor, setLinkedFor] = useState<EmissionEvidenceListItem | null>(null);
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  const history = useHistory();

  const handleToggleSelectMode = () => {
    setIsMultiSelectMode((prev) => !prev);
    if (selected.size > 0) {
      setSelected(new Set());
    }
  };

  const trimmedSearch = search.trim();
  const { data, isFetching, error: listError } = useListEmissionEvidenceQuery(
    {
      initiativeId,
      evidenceType: evidenceType || undefined,
      search: trimmedSearch || undefined,
      limit: PAGE_SIZE,
      offset,
    },
    { skip: !initiativeId }
  );

  const [deleteEvidence] = useDeleteEmissionEvidenceMutation();
  const { download } = useDownloadEvidence(initiativeId);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const hasMorePages = total > items.length;

  const { filtered, availableExts, byType } = useMemo(
    () => deriveItems(items, fileType),
    [items, fileType],
  );
  const sortedItems = useMemo(() => sortItems(filtered, sort), [filtered, sort]);

  const { storageLabel, linkedCount } = useMemo(() => {
    let bytes = 0;
    let linked = 0;
    for (const item of items) {
      bytes += item.fileSize ?? 0;
      if (item.linkedActivityCount > 0) {
        linked += 1;
      }
    }
    return {
      storageLabel: bytes === 0 ? '0 KB' : String(bytesToHumanReadable(bytes)),
      linkedCount: linked,
    };
  }, [items]);
  const unlinkedCount = items.length - linkedCount;

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === sortedItems.length && sortedItems.length > 0) {
      setSelected(new Set());
      return;
    }
    setSelected(new Set(sortedItems.map((it) => it._id)));
  };

  const selectedItems = useMemo(
    () => sortedItems.filter((it) => selected.has(it._id)),
    [sortedItems, selected],
  );

  const handleConfirmDelete = async () => {
    if (!pendingDelete) {
      return;
    }
    const target = pendingDelete;
    try {
      await deleteEvidence({ initiativeId, evidenceId: target._id }).unwrap();
      setSelected((prev) => {
        if (!prev.has(target._id)) {
          return prev;
        }
        const next = new Set(prev);
        next.delete(target._id);
        return next;
      });
      setPendingDelete(null);
    } catch (error) {
      toastFromError({ error, title: `Failed to delete ${target.originalName}` });
    }
  };

  const handleBulkDownload = async () => {
    const outcomes = await Promise.all(
      selectedItems.map(
        (ev): Promise<Outcome<EmissionEvidenceListItem>> =>
          download(ev._id, { silent: true }).then((r) =>
            r.ok
              ? ({ ok: true, value: ev } as const)
              : ({ ok: false, value: ev, error: r.error } as const),
          ),
      ),
    );
    const summary = summarizeOutcomes(outcomes, (ev) => ev.originalName, {
      successVerb: 'Downloaded',
      failureVerb: 'download',
    });
    if (summary.failed.length === 0) {
      return;
    }
    toastFromError({ error: summary.firstError, title: summary.failureTitle });
  };

  const handleBack = () => {
    history.push(generateUrl(emissionsTrackerRoutes.EMISSIONS_ADMIN, { initiativeId }));
  };

  const activeFilterCount =
    (evidenceType ? 1 : 0) + (fileType ? 1 : 0) + (trimmedSearch ? 1 : 0);

  const renderListBody = (): ReactNode => {
    if (isFetching) {
      return <div className='tw:text-sm tw:text-ThemeTextMedium'>Loading evidence…</div>;
    }
    if (listError) {
      return (
        <div className='tw:text-sm tw:text-ThemeAccentDanger' role='alert'>
          Couldn&apos;t load evidence. Try refreshing the page.
        </div>
      );
    }
    if (sortedItems.length === 0) {
      return (
        <NoData
          text={
            activeFilterCount > 0
              ? 'No documents match — try clearing filters or search.'
              : 'No evidence uploaded yet.'
          }
        />
      );
    }
    return (
      <ul className='tw:space-y-2 tw:list-none tw:pl-0'>
        {sortedItems.map((item) => (
          <EvidenceLibraryRow
            key={item._id}
            evidence={item}
            selected={selected.has(item._id)}
            selectable={isMultiSelectMode}
            onToggleSelect={() => toggleOne(item._id)}
            onDownload={() => download(item._id)}
            onDelete={() => setPendingDelete(item)}
            onShowLinks={() => setLinkedFor(item)}
          />
        ))}
      </ul>
    );
  };

  return (
    <Dashboard>
      <div className='tw:space-y-5'>
        <header className='tw:flex tw:items-center tw:gap-3 tw:mb-6 tw:w-full'>
          <Button variant='ghost' size='icon' onClick={handleBack}>
            <ArrowLeft className='tw:size-4' />
          </Button>
          <div className='tw:flex tw:flex-col tw:gap-1 tw:w-full'>
            <h4 className='tw:text-xl tw:font-semibold tw:mb-1'>Evidence Library</h4>
            <p className='tw:text-sm tw:text-muted-foreground'>All evidence, templates, and reports you&apos;ve uploaded — reusable across activities and sessions.</p>
          </div>
          <div className='tw:flex tw:justify-end'>
            <Button onClick={() => setIsUploadOpen(true)}>
              <Upload className='tw:h-4 tw:w-4' />
              Upload documents
            </Button>
          </div>
        </header>

        <StatsStrip
          total={total}
          storageLabel={storageLabel}
          byType={byType}
          linkedCount={linkedCount}
          unlinkedCount={unlinkedCount}
          pageScopeOnly={hasMorePages}
        />

        <Card>
          <CardContent className='tw:p-4 tw:space-y-4'>
            <Toolbar
              search={search}
              onSearchChange={(value) => {
                setSearch(value);
                setOffset(0);
              }}
              evidenceType={evidenceType}
              onEvidenceTypeChange={(value) => {
                setEvidenceType(value);
                setOffset(0);
              }}
              fileType={fileType}
              onFileTypeChange={setFileType}
              fileTypeOptions={availableExts}
              sort={sort}
              onSortChange={setSort}
            />

            <ListHeader
              resultCount={sortedItems.length}
              isSelectMode={isMultiSelectMode}
              onToggleSelectMode={handleToggleSelectMode}
              selectedCount={selected.size}
              allChecked={selected.size === sortedItems.length && sortedItems.length > 0}
              onToggleAll={toggleAll}
              onBulkDownload={handleBulkDownload}
            />

            <FilterSummary
              evidenceType={evidenceType}
              onClearType={() => {
                setEvidenceType('');
                setOffset(0);
              }}
              fileType={fileType}
              onClearFileType={() => setFileType('')}
              search={trimmedSearch}
              onClearSearch={() => {
                setSearch('');
                setOffset(0);
              }}
            />

            {renderListBody()}

            {data && data.total > PAGE_SIZE ? (
              <div className='tw:flex tw:items-center tw:justify-between tw:pt-2'>
                <div className='tw:text-xs tw:text-ThemeTextMedium'>
                  Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, data.total)} of {data.total}
                </div>
                <div className='tw:flex tw:gap-2'>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                    disabled={offset === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => setOffset(offset + PAGE_SIZE)}
                    disabled={offset + PAGE_SIZE >= data.total}
                  >
                    Next
                  </Button>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      <DeleteModal
        isOpen={!!pendingDelete}
        title={deleteDialogTitle(pendingDelete)}
        content={<DeleteDialogContent target={pendingDelete} />}
        handleCancel={() => setPendingDelete(null)}
        handleDelete={handleConfirmDelete}
      />
      <LibraryUploadModal
        isOpen={isUploadOpen}
        initiativeId={initiativeId}
        onClose={() => setIsUploadOpen(false)}
      />
      <LinkedActivitiesModal
        initiativeId={initiativeId}
        evidence={linkedFor}
        onClose={() => setLinkedFor(null)}
      />
    </Dashboard>
  );
};
